import getSymbolFromCurrency from "currency-symbol-map";
import { useTranslation } from "react-i18next";
import { STRIPE } from "../../../../constants";

const { ZERO_DECIMAL_CURRENCIES } = STRIPE;

const MONTHS_IN_YEAR = 12;

// Suffix the floor and its equivalent are quoted in, per band.
const BAND_SUFFIX_KEYS = {
  weekly: "perWeekShort",
  monthly: "perMonthShort",
  quarterly: "perQuarterShort",
  annual: "perYearShort",
};

/**
 * Live estimate and validation rail for the create-plan modal.
 *
 * Shows the floor warning only when the plan is below floor — there is
 * deliberately no positive "above floor" confirmation.
 */
export const PlanSummaryRail = ({
  summary,
  currency,
  price,
  interval,
  intervalCount,
  accessModel,
  passType,
}) => {
  const { t } = useTranslation("coaching-portal");
  const symbol = getSymbolFromCurrency(currency) || "$";

  // Yen and its peers have no minor unit — ¥2,000.00 is not a price anyone
  // writes. USD amounts keep their cents.
  const decimals = ZERO_DECIMAL_CURRENCIES.includes(
    String(currency || "").toUpperCase()
  )
    ? 0
    : 2;

  const money = (value) =>
    `${symbol}${Number(value).toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    })}`;
  // Floors read the way the PRD quotes them: $4.49, but $99 rather than
  // $99.00 — same rule in whatever currency the plan is priced in.
  const roundLabel = (value) =>
    Number.isInteger(value) ? `${symbol}${value}` : money(value);

  const {
    hasPrice,
    below,
    band,
    floorDisplay,
    normalizedEquivDisplay,
    cyclesPerYear,
    annualRevenue,
    isOneOff,
  } = summary;

  const suffixKey = BAND_SUFFIX_KEYS[band];
  const suffix = suffixKey
    ? t(`planManager.createPlan.summary.${suffixKey}`)
    : "";
  const intervalLabel = t(
    `planManager.createPlan.summary.perCycle_${interval}`,
    { count: Number(intervalCount) || 1 }
  );

  const extraCycles = cyclesPerYear - MONTHS_IN_YEAR;
  // The insight sells a custom cadence against the monthly plan a coach would
  // otherwise have built. A plain weekly plan is not that comparison — telling
  // its owner they bill "+40 cycles vs monthly" is noise, not an upsell.
  const showExtraCycles =
    hasPrice && !isOneOff && extraCycles > 0 && Number(intervalCount) > 1;

  return (
    <aside className="plan-summary-rail">
      <h3>{t("planManager.createPlan.summary.title")}</h3>
      <p className="subtitle">{t("planManager.createPlan.summary.subtitle")}</p>

      {accessModel && (
        <div className="access-model-card">
          <strong>
            {t(`planManager.createPlan.accessModels.${accessModel}.label`)}
          </strong>
          <span>
            {t(
              `planManager.createPlan.accessModels.${accessModel}.description`
            )}
          </span>
        </div>
      )}

      {below && (
        <div className="chip warning summary-warning">
          <div className="chip-text">
            <strong>
              {band === "annual"
                ? t("planManager.createPlan.summary.belowAnnualFloorTitle")
                : t("planManager.createPlan.summary.belowFloorTitle")}
            </strong>
            <span>
              {band
                ? t("planManager.createPlan.summary.belowFloorPush", {
                    equiv: `${roundLabel(normalizedEquivDisplay)}${suffix}`,
                    minimum: `${roundLabel(floorDisplay)}${suffix}`,
                  })
                : t("planManager.createPlan.summary.belowFloorPress", {
                    price: roundLabel(normalizedEquivDisplay),
                    minimum: roundLabel(floorDisplay),
                  })}
            </span>
          </div>
        </div>
      )}

      {isOneOff && (
        <>
          <div className="chip info summary-notice">
            <div className="chip-text">
              <strong>{t("planManager.createPlan.summary.oneOffTitle")}</strong>
              <span>{t("planManager.createPlan.summary.oneOffDetail")}</span>
            </div>
          </div>
          {/* The day pass is the one that surprises operators: no account, no
              key, nothing but the email they wrote. */}
          <div
            className={`chip ${passType === "day" ? "warning" : "info"} summary-pass-note`}
          >
            <div className="chip-text">
              <span>
                {passType === "day"
                  ? t("planManager.createPlan.summary.dayPassWarning")
                  : t("planManager.createPlan.summary.weekPassNote")}
              </span>
            </div>
          </div>
        </>
      )}

      {isOneOff && hasPrice && (
        <dl className="summary-lines">
          <div className="summary-line">
            <dt>{t("planManager.createPlan.summary.oneTimeCharge")}</dt>
            <dd>{money(price)}</dd>
          </div>
          <div className="summary-line">
            <dt>{t("planManager.createPlan.summary.accessWindow")}</dt>
            <dd>
              {passType === "day"
                ? t("planManager.createPlan.summary.accessWindowDay")
                : t("planManager.createPlan.summary.accessWindowWeek")}
            </dd>
          </div>
        </dl>
      )}

      {hasPrice && !isOneOff && (
        <>
          <dl className="summary-lines">
            <div className="summary-line">
              <dt>{t("planManager.createPlan.summary.perCycle")}</dt>
              <dd>{`${money(price)} ${intervalLabel}`}</dd>
            </div>
            {band && (
              <div className="summary-line">
                <dt>
                  {t(`planManager.createPlan.summary.equivalent_${band}`)}
                </dt>
                <dd>{`${roundLabel(normalizedEquivDisplay)}${suffix}`}</dd>
              </div>
            )}
            <div className="summary-line">
              <dt>{t("planManager.createPlan.summary.cyclesPerYear")}</dt>
              <dd>{cyclesPerYear}</dd>
            </div>
          </dl>

          <dl className="summary-lines total">
            <div className="summary-line">
              <dt>{t("planManager.createPlan.summary.annualRevenue")}</dt>
              <dd>{money(annualRevenue)}</dd>
            </div>
          </dl>
        </>
      )}

      {showExtraCycles && (
        <div className="chip info summary-info">
          <div className="chip-text">
            <strong>
              {t("planManager.createPlan.summary.extraCycleTitle", {
                count: extraCycles,
              })}
            </strong>
            <span>
              {t("planManager.createPlan.summary.extraCycleDetail", {
                cycles: cyclesPerYear,
                amount: money(Number(price) * extraCycles),
              })}
            </span>
          </div>
        </div>
      )}
    </aside>
  );
};
